import { logEvent } from "@shared/analytics/analytics";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { Button } from "@shared/ui/button";
import { Switch } from "@shared/ui/switch";
import { PAGE_SIZE } from "@shared/utils/constants";
import { Edit, ExternalLink, Eye, Factory, Plus } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useGetManuFacturer, useUpdateManufacturerStatus } from "../../hooks";
import { Manufacturer } from "../../types";
import { ManufacturerFilter } from "../filter/filter";

interface ManufacturersListProps {
  onEdit: (manufacturer: Manufacturer) => void;
  onAdd: () => void;
}

export const ManufacturersList: React.FC<ManufacturersListProps> = ({
  onEdit,
  onAdd,
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);

  const currentSearch = decodeURIComponent(searchParams.get("search") || "");
  const currentCountry = searchParams.get("country") || "";
  const currentStatus = searchParams.get("isActive") || "";

  // Pass search and country parameters to the hook
  const updateManufacturerStatus = useUpdateManufacturerStatus();
  useEffect(() => {
    logEvent("manufacturers_list_viewed");
  }, []);
  const queryParams = useMemo(
    () => ({
      page: currentPage,
      searchData: currentSearch,
      country: currentCountry,
      isActive: currentStatus,
    }),
    [currentPage, currentSearch, currentCountry, currentStatus]
  );

  const filters = useMemo(() => {
    const baseFilters: any = {
      search: queryParams.searchData,
    };

    if (queryParams.country !== "") {
      baseFilters.country = queryParams.country;
    }

    if (queryParams.isActive !== "") {
      baseFilters.isActive = queryParams.isActive;
    }

    return baseFilters;
  }, [queryParams]);

  const { data: manufacturersData } = useGetManuFacturer(
    currentPage,
    filters.search,
    filters.country,
    filters.isActive
  );

  const handleApplyFilters = (newFilters: any) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      ...newFilters,
      page: 1,
    };

    if (!newFilters.country || newFilters.country === "") {
      delete newParams.country;
    }

    if (!newFilters.isActive || newFilters.isActive === "") {
      delete newParams.isActive;
    }

    setSearchParams(newParams);
  };

  const handleResetFilters = () => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
    };

    delete newParams.country;
    delete newParams.search;
    delete newParams.isActive;
    newParams.page = 1;
    setCurrentPage(1);

    setSearchParams(newParams);
  };

  const handleSearch = (searchTerm: string) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      ...(currentCountry && { country: currentCountry }),
    };

    if (searchTerm.trim()) {
      newParams.search = searchTerm.trim();
    } else {
      delete newParams.search;
    }

    setSearchParams(newParams);
  };

  const handlePageChange = (page: number) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      page: String(page),
    };

    setSearchParams(newParams);
    setCurrentPage(page);
  };

  const columns: TableColumn<Manufacturer>[] = useMemo(
    () => [
      {
        key: "manufacturerNo",
        header: "Manufacturer ID",
        type: "text",
      },
      {
        key: "name",
        header: "Manufacturer Name",
        type: "text",
        render: (value) => <span className="font-medium">{value}</span>,
      },
      {
        key: "manufacturerContactPerson",
        header: "Contact Info",
        type: "custom",
        render: (value, row) => (
          <div>
            <p className="font-medium">{value || "NA"}</p>
            <p className="text-xs text-muted-foreground">
              {row.manufacturerContactEmail || "NA"}
            </p>
          </div>
        ),
      },
      {
        key: "contactNumber",
        header: "Phone Number",
        type: "custom",
        render: (value) => <span className="ml-6">{value || "N/A"}</span>,
      },
      {
        key: "website",
        header: "Website",
        type: "custom",
        render: (value) =>
          value ? (
            <a
              href={`https://${value}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline flex items-center gap-1"
            >
              {value}
              <ExternalLink className="h-3 w-3" />
            </a>
          ) : (
            "N/A"
          ),
      },
      {
        key: "country",
        header: "Country",
        type: "custom",
        render: (value) => <span>{value || "N/A"}</span>,
      },
      {
        key: "isActive",
        header: "Status",
        type: "custom",
        render: (value) => (
          <span
            className={`px-2 py-1 rounded text-xs font-medium ${
              value
                ? "bg-green-100 text-green-700"
                : "bg-gray-100 text-gray-800"
            }`}
          >
            {value ? "Active" : "Inactive"}
          </span>
        ),
      },
      {
        key: "assetCount",
        header: "Assets Produced",
        type: "custom",
        render: (value) => <span className="ml-6">{value || "N/A"}</span>,
      },
      {
        key: "actions",
        header: "Actions",
        render: (value, row) => {
          return (
            <div className="flex items-center gap-2">
              <Switch
                checked={row.isActive}
                onCheckedChange={(checked) =>
                  updateManufacturerStatus.mutateAsync({
                    id: row.id,
                    isActive: checked,
                  })
                }
              />
              <Button
                size="icon"
                variant="ghost"
                onClick={() => {
                  logEvent("manufacturer_edit_clicked", {
                    id: row.id,
                    name: row.name,
                  });
                  onEdit(row);
                }}
                title="Edit"
              >
                <Edit className="h-4 w-4" />
              </Button>

              <Button
                size="icon"
                variant="ghost"
                onClick={() => {
                  logEvent("manufacturer_view_clicked", {
                    id: row.id,
                    name: row.name,
                  });
                  navigate(`/ax/settings/manufacturers/view/${row.id}`);
                }}
                title="View"
              >
                <Eye className="h-4 w-4" />
              </Button>
            </div>
          );
        },
      },
    ],
    [updateManufacturerStatus.isPending] // Add dependency to re-render when pending state changes
  );

  // Pagination logic (client-side for now, but can be moved to server-side)
  const totalItems = manufacturersData?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const paginatedManufacturers = manufacturersData?.results;
  const pagination = useMemo(
    () => ({
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: startIndex + 1,
      endItem: Math.min(currentPage * PAGE_SIZE, totalItems),
    }),
    [currentPage, totalPages, totalItems, startIndex]
  );

  const FilterPanel = (
    <ManufacturerFilter
      currentFilters={{
        country: currentCountry,
        search: currentSearch,
        isActive: currentStatus,
      }}
      onApplyFilters={handleApplyFilters}
      onResetFilters={handleResetFilters}
    />
  );

  const hasActiveFilters = currentCountry !== "" || currentStatus !== "";

  return (
    <div className="space-y-6"> 
      {/* Generic Table */}
      <GenericTable
        data={paginatedManufacturers || []}
        columns={columns}
        rowKey="id"
        searchPlaceholder="Search by id or name..."
        searchParamName="query"
        searchDebounceMs={300}
        onSearch={handleSearch}
        pagination={pagination}
        onPageChange={handlePageChange}
        exportLoading={false}
        filterPanel={FilterPanel}
        emptyMessage="No manufacturers found"
        emptyIcon={<Factory className="h-8 w-8 text-muted-foreground mb-2" />}
        filters={
          hasActiveFilters ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                Filters active
              </span>
            </div>
          ) : null
        }
      />
    </div>
  );
};
